// YERİ: src/app/domains/website/sections/mkt-hero.sections.ts
// Hero, güven bandı (logo duvarı) ve kapanış CTA bandı.
//
// HERO ORTALANMIŞ. Önce sol-sağ bölünmüştü ve hem vaadi hem ürünü küçültüyordu:
// başlık dar bir sütuna sıkışıyor, ekran görüntüsü yarım kalıyordu. Ortalanmış
// hero başlığa tüm genişliği, ürüne de altında TAM bir sahne verir.
//
// Zemin: çok yumuşak renk sızıntıları (.mkt-hero-mesh). Düz beyaz bir hero
// "form sitesi" gibi görünüyordu — sayfanın görsel imzası buradan doğuyor.

import { Component, computed, inject, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { SiteConfig, cfg } from '@/app/domains/site/sections/section.model';
import { LanguageStore } from '../core/language.store';
import { MktMedia } from './mkt-media';
import { MktIcon } from './mkt-icon';
import { MktReveal } from './mkt-motion';
import { mktLink, MktRouterLink, resolveMktLink } from './mkt-link';
import { splitLines } from './mkt-price';
import { isSignUpLink, MktTrialHint } from './mkt-trial-hint';

@Component({
  selector: 'mkt-hero',
  standalone: true,
  imports: [RouterLink, MktMedia, MktIcon, MktReveal, MktTrialHint],
  // mkt-hero-mesh (yumuşak renk sızıntıları) arka plan GÖRSELİ YOKKEN çizilir;
  // ikisi üst üste binince zemin kirleniyor.
  host: {
    class: 'relative isolate block overflow-hidden',
    '[class.mkt-hero-mesh]': '!backgroundUrl()',
  },
  template: `
    @if (backgroundUrl()) {
      <!-- Arka plan görseli.
           • object-cover + odak noktası: 21:9'a yakın bir görseli 390px'lik bir
             telefonda da kullanılabilir kılmanın tek yolu kırpmak. Odağı
             alt-orta tutuyoruz; görselin anlamlı kısmı orada, üst tarafı zaten
             açık ve başlığa yer bırakıyor.
           • Perde (scrim): başlık ve gövde metni HER görselde okunur kalmalı.
             Üstten aşağı beyazdan şeffafa bir katman, en altta da sayfaya
             karışması için tekrar beyaza dönüş var — yoksa bölüm sınırında
             görsel bıçak gibi kesiliyor.
           • aria-hidden + boş alt: dekoratif, ekran okuyucuya bir şey katmaz.
           • fetchpriority=high: ilk ekranın en büyük öğesi (LCP). -->
      <!-- srcset DEĞİL <picture>: telefon sürümü küçültülmüş kopya değil, AYRI
           KIRPILMIŞ bir görsel. Geniş bir görseli telefonun uzun hero'suna
           object-cover ile sığdırmak, ortadan ince bir dikey şerit alıyordu —
           kompozisyon kayboluyor, üstelik 1920px'lik dosya boşuna iniyordu.
           srcset yalnız aynı görselin farklı boyutları için doğrudur; farklı
           kırpma "sanat yönü" (art direction) sorunudur ve çözümü <picture>. -->
      <picture class="pointer-events-none absolute inset-0 -z-10 block">
        @if (backgroundUrlSmall()) {
          <source media="(max-width: 640px)" [srcset]="backgroundUrlSmall()" />
        }
        <img [src]="backgroundUrl()" alt="" aria-hidden="true"
          fetchpriority="high" decoding="async"
          class="size-full object-cover object-[50%_65%]" />
      </picture>
      <!-- Perde oranları tarayıcıda ayarlandı: daha kuvvetli bir perde
           (%88/%62) bu pastel görseli tamamen yutuyordu, daha zayıfı başlığın
           altındaki gri gövde metnini okunmaz yapıyor. Aşağıdaki değerler
           ikisinin arasında duruyor. En alttaki tam beyaza dönüş şart —
           yoksa bölüm sınırında görsel bıçak gibi kesiliyor. -->
      <div class="pointer-events-none absolute inset-0 -z-10"
        style="background: linear-gradient(to bottom,
                 color-mix(in srgb, var(--mkt-paper) 72%, transparent) 0%,
                 color-mix(in srgb, var(--mkt-paper) 34%, transparent) 42%,
                 color-mix(in srgb, var(--mkt-paper) 26%, transparent) 66%,
                 var(--mkt-paper) 100%)"></div>
    }

    <div class="mx-auto max-w-7xl px-5 pb-16 pt-20 text-center sm:px-8 lg:pt-24">

      <div mktReveal>
        @if (eyebrow()) {
          <!-- Rozet: sol tarafı gradyan dolgulu hap + metin. Sayfanın ilk
               renkli öğesi — hemen "canlı" bir ton kurar. -->
          <span class="inline-flex items-center gap-2.5 rounded-full border border-[color:var(--mkt-border)] bg-white/70 py-1 ps-1 pe-4 backdrop-blur">
            @if (badge()) {
              <span class="rounded-full px-2.5 py-1 text-[0.6875rem] font-extrabold uppercase tracking-wide text-white"
                style="background: var(--mkt-gradient)">{{ badge() }}</span>
            }
            <span class="text-sm font-semibold text-[color:var(--mkt-text-muted)]">{{ eyebrow() }}</span>
          </span>
        }

        <h1 class="mkt-display mx-auto mt-8 max-w-4xl text-[2.75rem]/[1.1] sm:text-[3.6rem]/[1.1] lg:text-[4.15rem]/[1.1]">
          <!-- Vurgulu kısım AYRI SATIRDA ve gradyan: referansın en okunaklı
               başlık kalıbı. Tek satırda karışınca ikisi de zayıflıyor. -->
          @if (highlight()) {
            <span class="mkt-gradient-text">{{ highlight() }}</span><br>
          }
          {{ headline() }}
        </h1>

        @if (subtext()) {
          <p class="mkt-lead mx-auto mt-7 max-w-2xl sm:text-xl">{{ subtext() }}</p>
        }

        <div class="mt-10 flex flex-col items-center justify-center gap-5 sm:flex-row sm:gap-7">
          @if (primaryCtaText()) {
            <a [routerLink]="primaryLink()" class="mkt-btn mkt-btn-primary !px-7 !py-3.5 !text-base">
              {{ primaryCtaText() }}
            </a>
          }
          @if (secondaryCtaText()) {
            <!-- İkincil eylem BUTON DEĞİL, oklu bağlantı: iki dolu buton yan
                 yana durunca ikisi de zayıflıyor. -->
            <a [routerLink]="secondaryLink()" class="mkt-link !text-base">
              {{ secondaryCtaText() }}
              <mkt-icon name="arrow" [size]="16" />
            </a>
          }
        </div>

        @if (primaryCtaText() && primaryIsSignUp()) {
          <!-- Free trial hint: separate element, CMS text untouched. -->
          <div class="mt-4 flex justify-center"><mkt-trial-hint /></div>
        }

        @if (trustPoints().length) {
          <p class="mt-5 text-sm text-[color:var(--mkt-text-faint)]">
            {{ trustPoints().join(' · ') }}
          </p>
        }
      </div>

      <!-- Ürün: başlığın ALTINDA, tam sahne. Medya yüklenmemişse kodla çizilir. -->
      <div class="mx-auto mt-16 max-w-5xl" [mktReveal]="120">
        <mkt-media [config]="config()" [eager]="true" />
      </div>
    </div>
  `,
})
export class MktHeroSection {
  readonly config = input.required<SiteConfig>();

  /** Arka plan görseli — CMS'ten (Ana Şablon değil, sayfanın kendi hero'su). */
  protected backgroundUrl    = computed(() => cfg(this.config(), 'backgroundUrl', ''));

  /** Telefon için ayrı kırpılmış görsel. Boşsa geniş görsel her yerde kullanılır. */
  protected backgroundUrlSmall = computed(() => cfg(this.config(), 'backgroundUrlSmall', ''));

  protected badge            = computed(() => cfg(this.config(), 'badge', ''));
  protected eyebrow          = computed(() => cfg(this.config(), 'eyebrow', ''));
  protected headline         = computed(() => cfg(this.config(), 'headline', ''));
  protected highlight        = computed(() => cfg(this.config(), 'highlight', ''));
  protected subtext          = computed(() => cfg(this.config(), 'subtext', ''));
  protected primaryCtaText   = computed(() => cfg(this.config(), 'primaryCtaText', ''));
  protected secondaryCtaText = computed(() => cfg(this.config(), 'secondaryCtaText', ''));

  // Ayraç: gerçek satır sonu VEYA literal `\n`. Sektör sayfaları ikinciyi
  // saklıyor; tek biçime bölünce güven satırları tek satıra çöküyordu.
  protected trustPoints = computed(() => splitLines(this.config()['trustPoints']));

  protected primaryLink   = mktLink(() => cfg(this.config(), 'primaryCtaUrl', ''));
  protected primaryIsSignUp = computed(() => isSignUpLink(cfg(this.config(), 'primaryCtaUrl', '')));
  protected secondaryLink = mktLink(() => cfg(this.config(), 'secondaryCtaUrl', ''));
}

// ═══════════════════════════════════════════════════════════════════════════
// LOGO DUVARI
// ═══════════════════════════════════════════════════════════════════════════

@Component({
  selector: 'mkt-logo-wall',
  standalone: true,
  imports: [MktReveal],
  host: { class: 'block bg-[color:var(--mkt-paper)]' },
  template: `
    <div class="mx-auto max-w-7xl px-5 pb-16 sm:px-8">
      @if (title()) {
        <p class="text-center text-sm font-bold text-[color:var(--mkt-text)]">{{ title() }}</p>
      }
      <ul class="mt-8 flex flex-wrap items-center justify-center gap-x-14 gap-y-7">
        @for (logo of logos(); track $index) {
          <li [mktReveal]="$index * 50">
            @if (logo.imageUrl) {
              <img [src]="logo.imageUrl" [alt]="logo.name || ''"
                class="h-7 w-auto opacity-50 grayscale transition duration-300 hover:opacity-90 hover:grayscale-0"
                loading="lazy" decoding="async" />
            } @else {
              <!-- Logo dosyası yoksa isim yazılır — kırık görsel ikonundan iyidir. -->
              <span class="text-lg font-bold tracking-tight text-[color:var(--mkt-text-faint)]">
                {{ logo.name }}
              </span>
            }
          </li>
        }
      </ul>
    </div>
  `,
})
export class MktLogoWallSection {
  readonly config = input.required<SiteConfig>();

  protected title = computed(() => cfg(this.config(), 'title', ''));
  protected logos = computed<{ name?: string; imageUrl?: string }[]>(() => {
    const raw = this.config()['logos'];
    return Array.isArray(raw) ? raw : [];
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// KAPANIŞ CTA — koyu zemin + gradyan ÇERÇEVELİ üç kart
//
// Tek bir dev gradyan bant yerine üç kart: ziyaretçinin üç farklı niyeti var
// (dene / konuş / öğren) ve tek bir butona hepsini sığdıramazsın.
// ═══════════════════════════════════════════════════════════════════════════

@Component({
  selector: 'mkt-cta-band',
  standalone: true,
  imports: [RouterLink, MktIcon, MktReveal, MktTrialHint],
  host: { class: 'mkt-dark relative isolate block overflow-hidden mkt-glow' },
  template: `
    <div class="relative z-10 mx-auto max-w-7xl px-5 py-24 sm:px-8">

      <h2 class="mkt-h2 text-center text-3xl sm:text-[2.75rem]/[1.18]" mktReveal>{{ title() }}</h2>
      @if (subtitle()) {
        <p class="mkt-lead mx-auto mt-5 max-w-2xl text-center">{{ subtitle() }}</p>
      }

      <ul class="mt-14 grid gap-5 lg:grid-cols-3">
        @for (card of cards(); track $index; let i = $index) {
          <li [mktReveal]="i * 70">
            <div class="mkt-grad-card flex h-full flex-col p-7">
              @if (card.badge) {
                <span class="self-start rounded-full bg-white px-3 py-1 text-[0.6875rem] font-bold text-[color:var(--mkt-accent)]">
                  {{ card.badge }}
                </span>
              }
              <h3 class="mkt-h3 mt-5 text-xl">{{ card.title }}</h3>
              <!-- 'description' eski anahtar: bazı sayfaların kartları onunla
                   kaydedilmiş ve bileşen yalnız 'body' okuduğu için metin hiç
                   görünmüyordu. Migration 'body'ye taşıyor; bu yedek, veri
                   taşınmamış bir ortamda da metnin kaybolmaması için. -->
              @if (card.body || card.description) {
                <p class="mt-3 flex-1 leading-relaxed text-[color:var(--mkt-text-muted)]">{{ card.body || card.description }}</p>
              }
              @if (card.ctaText) {
                <a [routerLink]="link(card.ctaUrl)" class="mkt-btn mkt-btn-secondary mt-7 self-start">
                  {{ card.ctaText }}
                  <mkt-icon name="arrow" [size]="15" />
                </a>
                @if (isSignUp(card.ctaUrl)) {
                  <div class="mt-3"><mkt-trial-hint /></div>
                }
              }
            </div>
          </li>
        }
      </ul>

      @if (footnote()) {
        <p class="mt-10 text-center text-sm text-[color:var(--mkt-text-faint)]">{{ footnote() }}</p>
      }
    </div>
  `,
})
export class MktCtaBandSection {
  readonly config = input.required<SiteConfig>();

  // mktLink() bir signal fabrikasıdır ve inject() gerektirir — liste içindeki
  // her kart için ayrı signal kuramayız. Store'u burada bir kez alıp şablondan
  // düz bir metotla çözüyoruz.
  private langStore = inject(LanguageStore);

  protected title    = computed(() => cfg(this.config(), 'title', ''));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', ''));
  protected footnote = computed(() => cfg(this.config(), 'footnote', ''));

  protected cards = computed<
    { badge?: string; title?: string; body?: string; description?: string; ctaText?: string; ctaUrl?: string }[]
  >(() => {
    const raw = this.config()['cards'];
    return Array.isArray(raw) ? raw : [];
  });

  protected link(url?: string): MktRouterLink {
    return resolveMktLink(url ?? '', this.langStore.current());
  }

  protected isSignUp(url?: string): boolean {
    return isSignUpLink(url);
  }
}
